import { useTranslationStore } from '~/store/translation'
import { LanguageCodes } from '~/enums/LanguageCodes'
import type { TTranslationOptions } from '~/composables/useLang'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'
import TRANSLATION_NAMESPACES from '~/enums/TranslationEnum'
import type { MessagesResponseInterface } from '~/types/translationTypes'
import getDefaultLocale from '~/utils/locale/getDefaultLocale'

export const useI18n = () => {
  const translationStore = useTranslationStore()

  const initResources = async (): Promise<void> => {
    const response: MessagesResponseInterface = await $fetch('/api/translations/translationMessages', {
      ...fetchRetryParameters,
      headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      timeout: 15000,
    })

    translationStore.setResources(response?.resources)
  }

  const initMessages = async (): Promise<void> => {
    await $fetch('/api/translations/translationMessages', {
      ...fetchRetryParameters,
      headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      timeout: 60000,
    })
  }

  function getTranslation2(languageCode: string, key: string, opts: TTranslationOptions = {}): string {
    const cacheKey = JSON.stringify(['i18n.translations_by_language_code', languageCode, key, opts])
    let translation = translationStore.getByKey(cacheKey)

    const notation: string = `${languageCode}.${TRANSLATION_NAMESPACES.TRANSLATION}.${key}`

    if (translation === null) {
      let value = translationStore.getResources?.[notation] ?? null

      if (value === null) {
        value = key

        translation = String(value)
      } else {
        translation = String(value)

        if (typeof opts === 'object' && opts !== null) {
          for (const prop in opts) {
            translation = String(translation)
              .replace(new RegExp(`{{${prop}}}`, 'g'), <string>opts[prop])
              .replace(new RegExp(`{{- ${prop}}}`, 'g'), <string>opts[prop])
          }
        }
      }
    }

    return translation
  }

  const initServerTranslations = async (): Promise<void> => {
    translationStore.resetData()
    const localeSetting = useState<string>('locale.setting', () => getDefaultLocale().iso)
    translationStore.setLocale(localeSetting.value)
    await initResources()
  }

  const initClientTranslations = async (): Promise<void> => {
    const localeSetting = useState<string>('locale.setting', () => getDefaultLocale().iso)
    translationStore.setLocale(localeSetting.value)
    await initMessages()
  }

  const setCurrentLanguage = (lang: LanguageCodes): void => {
    translationStore.setLocale(lang)
  }

  const translate = (key: string, opts: TTranslationOptions, locale?: string): string => {
    return getTranslation2(locale ?? translationStore.getLocale, key, opts)
  }

  return {
    initServerTranslations,
    initClientTranslations,
    setCurrentLanguage,
    translate,
    translationStore,
  }
}
